import { useRequestGetUninstallStats, useRequestRunUninstallCheck, useRequestSetHideUninstalledFromDecks } from '@/hooks/httpRequest';
import { confirmAction, IconFlag, Skeleton } from '@/components/custom';
import { Button, Label, Switch } from '@/components/ui';
import { getTitleEnumValue, notify } from '@/functions';
import type { CountryEnum } from '@/enums';
import { countries } from '@/config';
import { Smartphone } from 'lucide-react';

const sectionLabelClass = 'text-xs font-medium text-muted-foreground uppercase tracking-wide';

const notifyError = (e: unknown) =>
	notify((e as Record<string, string>)?.internalMessage || 'Une erreur s\'est produite.', 'error');

const UninstallsCard = () => {
	const { stats, isLoading, error } = useRequestGetUninstallStats();
	const { setHideFromDecks, isLoading: isSaving } = useRequestSetHideUninstalledFromDecks();
	const { runCheck, isLoading: isChecking } = useRequestRunUninstallCheck();

	const handleHideChange = async (hide: boolean) => {
		if (hide && !(await confirmAction('Les membres sans l\'app ne seront plus proposés dans les decks.'))) return;
		try {
			await setHideFromDecks(hide);
			notify(hide ? 'Membres sans l\'app masqués.' : 'Membres sans l\'app de nouveau visibles.');
		} catch (e) {
			notifyError(e);
		}
	};

	const handleCheck = async () => {
		if (!(await confirmAction('Un push silencieux part vers chaque membre. Les résultats arrivent sous 24 h.'))) return;
		try {
			await runCheck();
			notify('Vérification lancée.');
		} catch (e) {
			notifyError(e);
		}
	};

	return (
		<div className="bg-white rounded-2xl shadow-soft border border-border">
			<div className="flex items-center justify-between px-4 py-3 border-b border-border">
				<h3 className="font-semibold text-sm">Désinstallations</h3>
				{isLoading
					? <Skeleton className="h-4 w-12" />
					: stats && <span className="text-xs text-muted-foreground">{stats.uninstalled} membres actifs</span>}
			</div>

			<div className="p-4 space-y-4">
				{error && !stats ? (
					<p className="text-sm text-destructive">Erreur lors du chargement.</p>
				) : (
					<>
						<div className="space-y-2">
							<p className={sectionLabelClass}>Genre</p>
							{isLoading ? (
								<Skeleton className="h-4 w-40" />
							) : (
								<div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
									{stats?.byGender.length
										? stats.byGender.map(({ gender, count }) => (
											<span key={gender ?? 'none'}>
												{getTitleEnumValue('GenderEnum', gender) ?? 'Inconnu'} <span className="font-semibold">{count}</span>
											</span>
										))
										: <span className="text-muted-foreground">Aucun</span>}
								</div>
							)}
						</div>

						<div className="space-y-2">
							<p className={sectionLabelClass}>Pays</p>
							{isLoading ? (
								<div className="space-y-1.5">
									<Skeleton className="h-4 w-32" />
									<Skeleton className="h-4 w-28" />
									<Skeleton className="h-4 w-24" />
								</div>
							) : (
								<div className="space-y-1 text-sm">
									{stats?.byCountry.length
										? stats.byCountry.map(({ countryIso3, count }) => (
											<div key={countryIso3 ?? 'none'} className="flex items-center gap-2">
												<IconFlag country={countryIso3 as CountryEnum} />
												<span className="flex-1 truncate">{(countryIso3 && countries.getName(countryIso3, 'fr')) || 'Inconnu'}</span>
												<span className="font-semibold">{count}</span>
											</div>
										))
										: <span className="text-muted-foreground">Aucun</span>}
								</div>
							)}
						</div>

						<div className="flex items-center justify-between text-sm">
							<span>Notifications coupées</span>
							{isLoading ? <Skeleton className="h-4 w-10" /> : <span className="font-semibold">{stats?.notificationsOff}</span>}
						</div>
					</>
				)}

				<div className="border-t border-border" />

				<div className="flex items-center justify-between gap-3">
					<div className="space-y-0.5">
						<Label htmlFor="hideUninstalledFromDecks">Masquer des decks</Label>
						<p className="text-[11px] text-muted-foreground">Les membres sans l'app</p>
					</div>
					{isLoading ? (
						<Skeleton className="h-6 w-11 rounded-full" />
					) : (
						<Switch
							id="hideUninstalledFromDecks"
							checked={stats?.hideFromDecks ?? false}
							disabled={!stats || isSaving}
							onCheckedChange={handleHideChange}
						/>
					)}
				</div>

				<Button type="button" variant="outline" className="w-full" disabled={isChecking} onClick={handleCheck}>
					<Smartphone className="w-4 h-4 mr-2" />
					{isChecking ? 'Lancement...' : 'Vérifier tous les téléphones'}
				</Button>
			</div>
		</div>
	);
};

export default UninstallsCard;
